import { createEngine, requiresAds, resolvePatTier } from "@vninvestcharts/engine-core";
import { useCallback, useEffect, useInsertionEffect, useMemo, useRef, useState } from "react";
// `requiresAds` import từ engine-core (SSOT `capabilities.no_ads`).
// TRƯỚC ĐÂY có bản copy `FREE_TIERS` tại đây — bỏ ở W0.2
// (PLAN_FRONTEND_PROPLUS_TRACKA_2026-09-28 §2.1).
const DEFAULT_OVERLAY_INPUTS = {
    measurementEnabled: false,
    selectedPriceLabels: null,
    drawingPrimitives: null,
};
// React 18 polyfill for useEffectEvent (React 19+ native API)
function useEffectEvent(fn) {
    const ref = useRef(null);
    useInsertionEffect(() => { ref.current = fn; }, [fn]);
    return useCallback((...args) => ref.current?.(...args), []);
}
import { usePaneChromeBridge } from "./usePaneChromeBridge";
import { mountAdEnforcer } from "./ad/mountAdEnforcer";
const LONG_PRESS_DELAY_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;
export function useEngineHost({ dataSource = null, effectivePat, validatePat, engineOptions, engineFactory, instance, locale = "vi", overlayInputs, priceScaleMode, priceScaleSlotId, onEngineDispose, onEngineReady, onOverlayStateChange, onPaneHover, onPaneSnapshotsChange, onPointerCancel, onPointerDown, onPointerLeave, onPointerMove, onPointerUp, onClick, onContextMenu, onDoubleClick, onTierValidated, onVisibleRangeChange, panes = [], theme = { mode: "light" }, campaign = null, adLocale, advActivationDate, }) {
    const containerRef = useRef(null);
    const engineRef = useRef(null);
    const [engineState, setEngineState] = useState(null);
    const resolvedOverlayInputs = overlayInputs ?? DEFAULT_OVERLAY_INPUTS;
    const effectiveEngineOptions = useMemo(() => ({
        ...(engineOptions ?? {}),
        pat: effectivePat ?? "",
        validatePat: validatePat,
    }), [engineOptions, effectivePat, validatePat]);
    const hoveredPaneIdRef = useRef(null);
    const adEnforcerRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressPointerIdRef = useRef(null);
    const longPressStartRef = useRef(null);
    const longPressContextRef = useRef(null);
    const { paneSnapshots } = usePaneChromeBridge({
        engine: engineState,
        onOverlayStateChange,
        onPaneSnapshotsChange,
        onVisibleRangeChange,
    });
    const emitContextMenu = useEffectEvent((context) => {
        onContextMenu?.(context);
    });
    const clearLongPress = useCallback(() => {
        if (longPressTimerRef.current !== null) {
            clearTimeout(longPressTimerRef.current);
        }
        longPressTimerRef.current = null;
        longPressPointerIdRef.current = null;
        longPressStartRef.current = null;
        longPressContextRef.current = null;
    }, []);
    const notifyPaneHover = useEffectEvent((paneId) => {
        if (hoveredPaneIdRef.current === paneId) {
            return;
        }
        hoveredPaneIdRef.current = paneId;
        onPaneHover?.(paneId);
    });
    const notifyEngineReady = useEffectEvent((engine) => {
        onEngineReady?.(engine);
        setEngineState(engine);
    });
    const notifyTierValidated = useEffectEvent((tier) => {
        onTierValidated?.(tier);
    });
    const notifyEngineDispose = useEffectEvent(() => {
        onEngineDispose?.();
    });
    useEffect(() => clearLongPress, [clearLongPress]);
    const PANE_HEADER_HEIGHT = 28;
    const resolvePointerContext = (event) => {
        const container = containerRef.current;
        if (container === null) {
            return null;
        }
        const rect = container.getBoundingClientRect();
        const localX = event.clientX - rect.left;
        const localY = event.clientY - rect.top;
        const hoveredSnapshot = paneSnapshots.find((snapshot) => {
            const right = snapshot.bounds.left + snapshot.bounds.width;
            const bottom = snapshot.bounds.top + snapshot.bounds.height;
            return localX >= snapshot.bounds.left && localX <= right && localY >= snapshot.bounds.top && localY <= bottom;
        });
        return {
            paneId: hoveredSnapshot?.paneId ?? null,
            clientX: event.clientX,
            clientY: event.clientY,
            localX,
            localY,
            pointerType: "pointerType" in event ? event.pointerType || "mouse" : "mouse",
            buttons: event.buttons,
            altKey: event.altKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
        };
    };
    const resolveHoveredPaneId = (event) => {
        const context = resolvePointerContext(event);
        if (context === null) {
            return null;
        }
        // Only activate header hover when cursor is within the header strip, not the full pane.
        const hoveredSnapshot = paneSnapshots.find((snapshot) => {
            const headerTop = snapshot.headerAnchor.top;
            const headerBottom = headerTop + PANE_HEADER_HEIGHT;
            const right = snapshot.bounds.left + snapshot.bounds.width;
            return context.localX >= snapshot.bounds.left && context.localX <= right && context.localY >= headerTop && context.localY <= headerBottom;
        });
        return hoveredSnapshot?.paneId ?? null;
    };
    useEffect(() => {
        // If a pre-existing instance is provided, mount it directly
        if (instance) {
            engineRef.current = instance;
            const container = containerRef.current;
            if (container) {
                instance.mount(container);
                setEngineState(instance);
                notifyEngineReady(instance);
                const unsubTier = instance.subscribe("tierValidated", (payload) => {
                    notifyTierValidated(payload.tier);
                });
                // Auto-inject ad enforcer
                const tier = resolvePatTier(effectivePat ?? "");
                adEnforcerRef.current = mountAdEnforcer(effectivePat ?? "", effectiveEngineOptions?.validatePat, requiresAds(tier), campaign, tier, adLocale, advActivationDate);
                return () => {
                    adEnforcerRef.current?.unmount();
                    adEnforcerRef.current = null;
                    unsubTier();
                };
            }
            return undefined;
        }
        const container = containerRef.current;
        if (container === null) {
            return undefined;
        }
        const engine = engineFactory ? engineFactory() : createEngine(effectiveEngineOptions);
        engineRef.current = engine;
        engine.mount(container);
        setEngineState(engine);
        notifyEngineReady(engine);
        const unsubTier = engine.subscribe("tierValidated", (payload) => {
            notifyTierValidated(payload.tier);
        });
        // Auto-inject ad enforcer
        const tier = resolvePatTier(effectivePat ?? "");
        adEnforcerRef.current = mountAdEnforcer(effectivePat ?? "", effectiveEngineOptions?.validatePat, requiresAds(tier), campaign, tier, adLocale, advActivationDate);
        return () => {
            hoveredPaneIdRef.current = null;
            notifyEngineDispose();
            adEnforcerRef.current?.unmount();
            adEnforcerRef.current = null;
            unsubTier();
            engine.dispose("unmount");
            engineRef.current = null;
        };
    }, [engineFactory, effectiveEngineOptions]);
    // Fetch remote enforcement config — commented out for dev testing
    // useEffect(() => {
    // 	fetchEnforcementConfig().then((config) => {
    // 		adEnforcerRef.current?.updateEnforcement(config.enabled);
    // 	});
    // }, []);
    const prevPatRef = useRef(effectivePat ?? "");
    useEffect(() => {
        const engine = engineRef.current;
        const nextPat = effectivePat ?? "";
        if (engine && prevPatRef.current !== nextPat) {
            engine.setPat(nextPat);
            prevPatRef.current = nextPat;
        }
        // Update ad enforcer with new PAT
        adEnforcerRef.current?.updatePat(effectivePat);
        // Update tier for trademark display
        adEnforcerRef.current?.updateTier(resolvePatTier(nextPat));
    }, [effectivePat]);
    // Sync ad campaign into the enforcer when it changes
    useEffect(() => {
        adEnforcerRef.current?.updateCampaign(campaign ?? null);
    }, [campaign]);
    useEffect(() => {
        const container = containerRef.current;
        const engine = engineRef.current;
        if (container === null || engine === null) {
            return undefined;
        }
        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const observer = new ResizeObserver(() => {
            engine.resize();
        });
        observer.observe(container);
        return () => {
            observer.disconnect();
        };
    }, [engineState]);
    // PRO-BLACKSCREEN: every state-sync effect below MUST depend on engineState.
    // effectivePat "" → pro-PAT remounts the engine (mount effect deps on
    // effectiveEngineOptions) while `panes` identity is unchanged — without the
    // engine dep the new instance keeps paneSpecs=[] forever (layout → snapshots
    // [] → domains empty → blackscreen with data intact).
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null) {
            return;
        }
        engine.setPaneSpecs(panes);
    }, [panes, engineState]);
    useEffect(() => {
        if (hoveredPaneIdRef.current && !paneSnapshots.some((snapshot) => snapshot.paneId === hoveredPaneIdRef.current)) {
            notifyPaneHover(null);
        }
    }, [paneSnapshots]);
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null) {
            return;
        }
        engine.setDataSource(dataSource);
    }, [dataSource, engineState]);
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null) {
            return;
        }
        engine.setTheme(theme);
    }, [theme, engineState]);
    // 5b-ii: per-chart price scale projection (default linear = legacy behavior).
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null || priceScaleMode === undefined) {
            return;
        }
        engine.setPriceScaleMode(priceScaleMode, priceScaleSlotId);
    }, [priceScaleMode, priceScaleSlotId, engineState]);
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null) {
            return;
        }
        engine.setLocale(locale);
    }, [locale, engineState]);
    useEffect(() => {
        const engine = engineRef.current;
        if (engine === null) {
            return;
        }
        engine.setOverlayInputs(resolvedOverlayInputs);
    }, [resolvedOverlayInputs, engineState]);
    return {
        containerRef,
        engineRef,
        handlePointerEnter: (event) => {
            notifyPaneHover(resolveHoveredPaneId(event));
        },
        handlePointerMove: (event) => {
            notifyPaneHover(resolveHoveredPaneId(event));
            const context = resolvePointerContext(event);
            if (context !== null) {
                if (event.pointerType !== "mouse"
                    && longPressTimerRef.current !== null
                    && longPressPointerIdRef.current === event.pointerId
                    && longPressStartRef.current) {
                    const deltaX = event.clientX - longPressStartRef.current.x;
                    const deltaY = event.clientY - longPressStartRef.current.y;
                    if (Math.hypot(deltaX, deltaY) > LONG_PRESS_MOVE_THRESHOLD_PX) {
                        clearLongPress();
                    }
                    else {
                        longPressContextRef.current = context;
                    }
                }
                onPointerMove?.(context);
            }
        },
        handlePointerDown: (event) => {
            const context = resolvePointerContext(event);
            if (context !== null) {
                onPointerDown?.(context);
                if (event.pointerType !== "mouse") {
                    clearLongPress();
                    longPressPointerIdRef.current = event.pointerId;
                    longPressStartRef.current = { x: event.clientX, y: event.clientY };
                    longPressContextRef.current = context;
                    longPressTimerRef.current = setTimeout(() => {
                        const pendingContext = longPressContextRef.current;
                        if (pendingContext) {
                            emitContextMenu(pendingContext);
                        }
                        clearLongPress();
                    }, LONG_PRESS_DELAY_MS);
                }
            }
        },
        handlePointerUp: (event) => {
            clearLongPress();
            const context = resolvePointerContext(event);
            if (context !== null) {
                onPointerUp?.(context);
            }
        },
        handlePointerCancel: (event) => {
            clearLongPress();
            const context = resolvePointerContext(event);
            if (context !== null) {
                onPointerCancel?.(context);
            }
        },
        handlePointerLeave: (event) => {
            clearLongPress();
            notifyPaneHover(null);
            const context = resolvePointerContext(event);
            if (context !== null) {
                onPointerLeave?.(context);
            }
        },
        handlePointerOut: (event) => {
            clearLongPress();
            notifyPaneHover(null);
            const context = resolvePointerContext(event);
            if (context !== null) {
                onPointerLeave?.(context);
            }
        },
        handleClick: (event) => {
            clearLongPress();
            const context = resolvePointerContext(event);
            if (context !== null) {
                onClick?.(context);
            }
        },
        handleDoubleClick: (event) => {
            clearLongPress();
            const context = resolvePointerContext(event);
            if (context !== null) {
                onDoubleClick?.(context);
            }
        },
        handleContextMenu: (event) => {
            clearLongPress();
            const context = resolvePointerContext(event);
            if (context !== null) {
                onContextMenu?.(context);
            }
        },
    };
}
